/*
 * The Marketing suite as React Query hooks (draft 12). Each change refreshes the answers of its own part.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AdsAnswer,
  FinderRow,
  GbpAnswer,
  GbpBulk,
  GbpBulkAnswer,
  GbpPatch,
  GbpProfileRow,
  GbpProfilesAnswer,
  GoogleLocationRow,
  GbpChoice,
  GbpPostRow,
  SigninAnswer,
  SigninChoices,
  SigninKind,
  MediaRow,
  OutreachAnswer,
  PageBriefRow,
  ProspectRow,
  RankCircle,
  RankGridsAnswer,
  SocialPlatform,
  SocialPostRow,
  VisitPhotoPick,
  WebRankAnswer,
} from "@steward/shared";
import { api } from "./client";

type Method = "POST" | "PUT" | "PATCH" | "DELETE";

/** A change that refreshes these answers when it works. */
function useChange<V, T>(keys: string[], run: (v: V) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: [k] }))),
  });
}
const call =
  <T>(method: Method, path: string) =>
  (body?: unknown) =>
    api<T>(method, path, body);

export const useGbp = (companyId: string) =>
  useQuery({ queryKey: ["mk-gbp", companyId], queryFn: () => api<GbpAnswer>("GET", "marketing/gbp") });
export const useSyncGbp = () => useChange(["mk-gbp"], () => api<GbpAnswer>("POST", "marketing/gbp/sync"));
/** Google listings to pick from, searched only while the picker is open. */
export const useGbpChoices = (companyId: string, q: string, on: boolean) =>
  useQuery({
    queryKey: ["mk-gbp-choices", companyId, q],
    queryFn: () => api<GbpChoice[]>("GET", `marketing/gbp/choices${q ? `?q=${encodeURIComponent(q)}` : ""}`),
    enabled: on,
    staleTime: 5 * 60_000,
  });
export const usePickGbp = () =>
  useChange(["mk-gbp", "mk-dash", "mk-signin-choices"], (placeId: string) =>
    api<GbpAnswer>("POST", "marketing/gbp/pick", { placeId }),
  );
export const useSaveGbp = () =>
  useChange(["mk-gbp"], (v: { description: string; services: string[] }) => call<GbpAnswer>("PUT", "marketing/gbp")(v));
export const useReviewReply = () =>
  useChange(["mk-gbp"], (v: { id: string; reply: string; approve: boolean }) =>
    api<GbpAnswer>("PUT", `marketing/reviews/${v.id}/reply`, { reply: v.reply, approve: v.approve }),
  );
export const useGbpPost = () =>
  useChange(
    ["mk-gbp"],
    (v: { service: string; area: string; body: string; mediaId: string | null; scheduledFor: string | null }) =>
      call<GbpPostRow>("POST", "marketing/gbp/posts")(v),
  );

export const useCancelGbpPost = () => useChange(["mk-gbp"], (id: string) => api<unknown>("DELETE", `marketing/gbp/posts/${id}`));

export const useMedia = (companyId: string) =>
  useQuery({
    queryKey: ["mk-media", companyId],
    queryFn: () => api<{ media: MediaRow[]; visitPhotos: VisitPhotoPick[] }>("GET", "marketing/media"),
  });
export const useAddMedia = () =>
  useChange(["mk-media"], (v: { fileIds: string[]; tags: string[] }) =>
    call<{ media: MediaRow[] }>("POST", "marketing/media")(v),
  );
export const usePatchMedia = () =>
  useChange(["mk-media"], (v: { id: string; tags?: string[]; caption?: string }) =>
    api<MediaRow>("PATCH", `marketing/media/${v.id}`, { tags: v.tags, caption: v.caption }),
  );
export const useUploadMedia = () =>
  useChange(["mk-media"], (v: { name: string; mime: string; dataBase64: string; caption: string }) =>
    call<{ media: MediaRow[] }>("POST", "marketing/media/upload")(v),
  );
export const useRemoveMedia = () =>
  useChange(["mk-media"], (id: string) => api<{ media: MediaRow[] }>("DELETE", `marketing/media/${id}`));
export const useSocial = (companyId: string) =>
  useQuery({ queryKey: ["mk-social", companyId], queryFn: () => api<{ posts: SocialPostRow[] }>("GET", "marketing/social") });
export interface SocialInput {
  body: string;
  mediaIds: string[];
  platforms: SocialPlatform[];
  scheduledFor: string | null;
  draft: boolean;
}
/** A new post, or a change to one (with its id). The photo counts change with it. */
export const useSaveSocial = () =>
  useChange(["mk-social", "mk-media"], (v: SocialInput & { id?: string | undefined }) => {
    const { id, ...body } = v;
    return id ? api<SocialPostRow>("PUT", `marketing/social/${id}`, body) : call<SocialPostRow>("POST", "marketing/social")(body);
  });
export const useRemoveSocial = () =>
  useChange(["mk-social", "mk-media"], (id: string) => api<unknown>("DELETE", `marketing/social/${id}`));
export const useRetrySocial = () =>
  useChange(["mk-social"], (id: string) => api<SocialPostRow>("POST", `marketing/social/${id}/retry`));
export const useScheduleMonth = () =>
  useChange(["mk-social"], (month: string) => api<{ scheduled: number }>("POST", "marketing/social/schedule-month", { month }));

export const useGrids = (companyId: string) =>
  useQuery({ queryKey: ["mk-grids", companyId], queryFn: () => api<RankGridsAnswer>("GET", "marketing/grids") });
/** A new tracked search, checked from a circle for each listing (the first one's middle, size and miles out too). */
export const useAddGrid = () =>
  useChange(["mk-grids"], (v: { keyword: string; areas: RankCircle[] }) => {
    const f = v.areas[0]!;
    return call<RankGridsAnswer>(
      "POST",
      "marketing/grids",
    )({
      keyword: v.keyword,
      lat: f.lat,
      lng: f.lng,
      size: f.size,
      radius: f.radius,
      skip: f.skip,
      areas: v.areas,
    });
  });
export const useGridArea = () =>
  useChange(["mk-grids"], (areas: RankCircle[]) => api<RankGridsAnswer>("PUT", "marketing/grids/area", { areas }));
export const useRunGrid = () =>
  useChange(["mk-grids"], (id: string) => api<RankGridsAnswer>("POST", `marketing/grids/${id}/run`));
export const useRemoveGrid = () =>
  useChange(["mk-grids"], (id: string) => api<RankGridsAnswer>("DELETE", `marketing/grids/${id}`));
export const useWebRank = (companyId: string) =>
  useQuery({ queryKey: ["mk-web", companyId], queryFn: () => api<WebRankAnswer>("GET", "marketing/web-rank") });
export const useSetKeywords = () =>
  useChange(["mk-web"], (keywords: string[]) => api<WebRankAnswer>("PUT", "marketing/web-rank", { keywords }));
export const useRefreshWeb = () => useChange(["mk-web"], () => api<WebRankAnswer>("POST", "marketing/web-rank/refresh"));
export const useBriefs = (companyId: string) =>
  useQuery({ queryKey: ["mk-briefs", companyId], queryFn: () => api<{ briefs: PageBriefRow[] }>("GET", "marketing/briefs") });
export const useMakeBrief = () =>
  useChange(["mk-briefs"], (v: { url: string; keyword: string }) => call<PageBriefRow>("POST", "marketing/briefs")(v));
export const useRescore = () =>
  useChange(["mk-briefs"], (id: string) => api<PageBriefRow>("POST", `marketing/briefs/${id}/score`));

/** Who is signed in with Google and Facebook for Marketing (2026-11-01). */
export const useSignin = (companyId: string) =>
  useQuery({ queryKey: ["mk-signin", companyId], queryFn: () => api<SigninAnswer>("GET", "marketing/signin") });
/** What the sign in reaches, read only while the picker is open (it costs Google and Meta calls). */
export const useSigninChoices = (companyId: string, on: boolean) =>
  useQuery({
    queryKey: ["mk-signin-choices", companyId],
    queryFn: () => api<SigninChoices>("GET", "marketing/signin/choices"),
    enabled: on,
    staleTime: 5 * 60_000,
  });
export const useSigninStart = () =>
  useMutation({
    mutationFn: (kind: SigninKind) =>
      api<{ url: string }>("POST", "marketing/signin/start", { kind, back: window.location.origin }),
  });
export const useSignOut = () =>
  useChange(["mk-signin", "mk-signin-choices", "mk-gbp", "mk-ads", "mk-web"], (kind: SigninKind) =>
    api<{ removed: boolean }>("DELETE", `marketing/signin/${kind}`),
  );

export const useAds = (companyId: string) =>
  useQuery({ queryKey: ["mk-ads", companyId], queryFn: () => api<AdsAnswer>("GET", "marketing/ads") });

export interface SequenceInput {
  name: string;
  fromName: string;
  fromEmail: string;
  mailingAddress: string;
  steps: { days: number; subject: string; body: string }[];
  dailyCap: number;
  ramp: boolean;
}
export const useOutreach = (companyId: string) =>
  useQuery({ queryKey: ["mk-outreach", companyId], queryFn: () => api<OutreachAnswer>("GET", "marketing/outreach") });
export const useAddSequence = () =>
  useChange(["mk-outreach"], (v: SequenceInput) => call<OutreachAnswer>("POST", "marketing/outreach/sequences")(v));
export const useSending = () =>
  useChange(["mk-outreach"], (v: { id: string; on: boolean }) =>
    api<OutreachAnswer>("PUT", `marketing/outreach/sequences/${v.id}/sending`, { on: v.on }),
  );
export const useAddProspect = () =>
  useChange(["mk-outreach"], (v: { business: string; email: string; website: string | null; zip: string | null }) =>
    call<ProspectRow>("POST", "marketing/outreach/prospects")(v),
  );
export const useEnroll = () =>
  useChange(["mk-outreach"], (v: { sequenceId: string; prospectIds: string[] }) =>
    call<{ started: number }>("POST", "marketing/outreach/enroll")(v),
  );
export const useAiLines = () =>
  useChange(["mk-outreach"], (prospectIds: string[]) =>
    api<{ prospects: ProspectRow[] }>("POST", "marketing/outreach/ai-lines", { prospectIds }),
  );
export const useProspectReply = () =>
  useChange(["mk-outreach"], (v: { id: string; from: string; subject: string; body: string }) =>
    api<ProspectRow>("POST", `marketing/outreach/prospects/${v.id}/reply`, { from: v.from, subject: v.subject, body: v.body }),
  );
export const useFinder = (companyId: string) =>
  useQuery({ queryKey: ["mk-finder", companyId], queryFn: () => api<{ searches: FinderRow[] }>("GET", "marketing/finder") });
export const useFind = () =>
  useChange(["mk-finder", "mk-outreach"], (v: { category: string; area: string; big: boolean }) =>
    call<FinderRow>("POST", "marketing/finder")(v),
  );

// The Google profile editor (2026-11-01) --------------------------------------------------------------

export const useGbpProfiles = (companyId: string) =>
  useQuery({
    queryKey: ["mk-gbp-profiles", companyId],
    queryFn: () => api<GbpProfilesAnswer>("GET", "marketing/gbp/profiles"),
    staleTime: 60_000,
  });
export const useGoogleLocations = (companyId: string, on: boolean) =>
  useQuery({
    queryKey: ["mk-google-locations", companyId],
    queryFn: () => api<GoogleLocationRow[]>("GET", "marketing/gbp/google-locations"),
    enabled: on,
  });
export const useGbpAttach = () =>
  useChange(
    ["mk-gbp-profiles", "mk-google-locations", "mk-gbp", "mk-signin-choices"],
    (v: { name: string; companyId: string; subLocation: string | null }) =>
      api<GbpProfilesAnswer>("POST", "marketing/gbp/profiles", v),
  );
export const useGbpDetach = () =>
  useChange(["mk-gbp-profiles", "mk-google-locations", "mk-gbp", "mk-signin-choices"], (id: string) =>
    api<GbpProfilesAnswer>("DELETE", `marketing/gbp/profiles/${id}`),
  );
export const useGbpPatch = () =>
  useChange(["mk-gbp-profiles"], (v: { id: string; patch: GbpPatch }) =>
    api<GbpProfileRow>("PATCH", `marketing/gbp/profiles/${v.id}`, v.patch),
  );
export const useGbpBulk = () =>
  useChange(["mk-gbp-profiles"], (v: GbpBulk) => api<GbpBulkAnswer>("POST", "marketing/gbp/bulk", v));
